import React from 'react';
import PT from 'prop-types';

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var PortalContext = React.createContext();

var usePortalRegistery = function usePortalRegistery() {
  var context = React.useContext(PortalContext);

  if (!context) {
    throw new Error('Portal components should be rendered inside the Portal compount component');
  }

  return context;
};

var useRegistery = function useRegistery() {
  var containers = React.useRef({}).current;
  var children = React.useRef({}).current;

  var _React$useState = React.useState(0),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      currentId = _React$useState2[0],
      setCurrentId = _React$useState2[1];

  var renderContainer = function renderContainer(name) {
    return children[name] ? Object.keys(children[name]).sort().map(function (id) {
      return children[name][id];
    }) : [];
  };

  var addContainer = React.useMemo(function (name, container) {
    containers[name] = container;
  }, [containers]);

  var removeContainer = function removeContainer(name) {
    containers[name] = null;
  };

  var addChild = React.useMemo(function (name, id, child) {
    children[name][id] = child;
  }, [children]);

  var clearChild = function clearChild(name, id) {
    delete children[name][id];
  };

  var register = React.useMemo(function (name, child) {
    children[name] = children[name] || {};
    var id = "".concat(name, "_").concat(currentId);
    children[name][id] = child;
    setCurrentId(currentId + 1);
    return id;
  }, [setCurrentId, currentId]);
  var unregister = React.useMemo(function (name, id) {
    clearChild(name, id);
  }, [clearChild]);
  return React.useMemo(function () {
    return {
      addChild: addChild,
      renderContainer: renderContainer,
      addContainer: addContainer,
      removeContainer: removeContainer,
      clearChild: clearChild,
      register: register,
      unregister: unregister
    };
  }, [addChild, renderContainer, addContainer, removeContainer, register, unregister, clearChild]);
};

var PortalProvider = function PortalProvider(_ref) {
  var children = _ref.children;
  var value = useRegistery();
  return React.createElement(PortalContext.Provider, {
    value: value
  }, children);
};

PortalProvider.propTypes = {
  children: PT.oneOfType([PT.node, PT.arrayOf(PT.node)])
};

var Portal = function Portal(_ref) {
  var destination = _ref.destination,
      children = _ref.children;
  var data = React.useRef({}).current;

  var _usePortalRegistery = usePortalRegistery(),
      register = _usePortalRegistery.register,
      unregister = _usePortalRegistery.unregister,
      addChild = _usePortalRegistery.addChild;

  React.useEffect(function () {
    data.id = register(destination, children);
    renderNodeInPortal();
    return function () {
      unregister(destination, data.id);
    };
  }, [children]);
  var renderNodeInPortal = React.useCallback(function () {
    addChild(destination, children);
  }, [addChild, destination, children]);
  return null;
};

Portal.propTypes = {
  destination: PT.string.isRequired,
  children: PT.node
};
var _this = undefined;

var PortalDestination = function PortalDestination(_ref) {
  var name = _ref.name;

  var _React$useState = React.useState(0),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      amountOfChildren = _React$useState2[0],
      setAmountOfChildren = _React$useState2[1];

  var _usePortalRegistery = usePortalRegistery(),
      addContainer = _usePortalRegistery.addContainer,
      removeContainer = _usePortalRegistery.removeContainer,
      renderContainer = _usePortalRegistery.renderContainer;

  React.useEffect(function () {
    addContainer(name, _this);
    return function () {
      removeContainer(name, _this);
    };
  });
  React.useEffect(function () {
    var length = renderContainer(name).length;

    if (length !== amountOfChildren) {
      setAmountOfChildren(length);
    }
  }, [renderContainer(name)]);
  return renderContainer(name);
};

PortalDestination.propTypes = {
  name: PT.string.isRequired
};
export { Portal, PortalDestination, PortalProvider };